import { useState } from "react";
import { toast } from "sonner";
import { useLang } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const T = {
  en: {
    label: "Product updates by email",
    consent: "I agree to the processing of my e-mail address to receive Hadron product news (unsubscribe anytime).",
    cta: "Subscribe",
    ok: "Subscribed — thank you!",
    err: "Please enter a valid e-mail address and tick the consent box.",
  },
  pl: {
    label: "Nowości o produktach mailem",
    consent: "Wyrażam zgodę na przetwarzanie mojego adresu e-mail w celu otrzymywania nowości o produktach Hadron (wypiszę się w każdej chwili).",
    cta: "Zapisz się",
    ok: "Zapisano — dziękujemy!",
    err: "Podaj poprawny adres e-mail i zaznacz zgodę.",
  },
  de: {
    label: "Produktneuigkeiten per E-Mail",
    consent: "Ich stimme der Verarbeitung meiner E-Mail-Adresse zu, um Hadron-Produktneuigkeiten zu erhalten (jederzeit abbestellbar).",
    cta: "Abonnieren",
    ok: "Angemeldet — danke!",
    err: "Bitte gültige E-Mail-Adresse eingaben und Zustimmung ankreuzen.",
  },
  fr: {
    label: "Actualités produits par e-mail",
    consent: "J'accepte le traitement de mon adresse e-mail pour recevoir les actualités produits Hadron (désinscription à tout moment).",
    cta: "S'abonner",
    ok: "Inscription confirmée — merci !",
    err: "Veuillez saisir une adresse e-mail valide et cocher la case de consentement.",
  },
  it: {
    label: "Novità sui prodotti via e-mail",
    consent: "Acconsento al trattamento del mio indirizzo e-mail per ricevere le novità sui prodotti Hadron (disiscrizione in qualsiasi momento).",
    cta: "Iscriviti",
    ok: "Iscrizione completata — grazie!",
    err: "Inserisci un indirizzo e-mail valido e spunta la casella di consenso.",
  },
  hr: {
    label: "Novosti o proizvodima e-poštom",
    consent: "Prihvaćam obradu moje e-adrese za primanje novosti o Hadron proizvodima (odjava u svakom trenutku).",
    cta: "Pretplati se",
    ok: "Pretplaćeno — hvala!",
    err: "Unesite ispravnu e-adresu i označite privolu.",
  },
};

export const Newsletter = ({ source, topic = "" }) => {
  const { lang } = useLang();
  const t = T[lang] || T.en;
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) || !consent) {
      toast.error(t.err, { "data-testid": "newsletter-error" });
      return;
    }
    setBusy(true);
    try {
      const r = await fetch(`${API}/newsletter`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, language: lang, source, topic, consent: true }),
      });
      if (!r.ok) throw new Error();
      toast.success(t.ok, { "data-testid": "newsletter-success" });
      setEmail("");
      setConsent(false);
    } catch {
      toast.error(t.err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-6 pt-5 border-t border-line" data-testid={`newsletter-form-${source}`}>
      <p className="mono-label !text-[9px]">{t.label}</p>
      <div className="mt-3 flex flex-col sm:flex-row gap-2.5">
        <input
          data-testid={`newsletter-input-${source}`}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="name@company.eu"
          className="flex-1 min-w-0 bg-ink border border-line px-4 py-3 text-sm text-txt placeholder:text-txt-3 focus:outline-none focus:border-hadron-cyan"
        />
        <button
          type="submit"
          data-testid={`newsletter-submit-${source}`}
          disabled={busy}
          className="shrink-0 inline-flex items-center justify-center gap-2 border border-hadron-cyan text-hadron-cyan px-5 py-3 font-mono2 text-[11px] uppercase tracking-[0.2em] hover:bg-hadron-cyan hover:text-ink transition-colors disabled:opacity-60"
        >
          {t.cta}
        </button>
      </div>
      <label className="mt-3 flex items-start gap-2.5 cursor-pointer select-none">
        <input
          data-testid={`newsletter-consent-${source}`}
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          className="mt-0.5 h-4 w-4 shrink-0 accent-[#A8D67F]"
        />
        <span className="text-[11px] leading-relaxed text-txt-3">{t.consent}</span>
      </label>
    </form>
  );
};
